import { FC } from "react";
import { makeStyles } from "tss-react/mui";
import { ORIENTATION } from "@miplatform/constants";
import { mdiClose } from "@mdi/js";
import { Box, Collapse, CollapseProps } from "@mui/material";
import { MiActions } from "../MiActions/MiActions";
import { MiDrawerHeader } from "../MiDrawerHeader/MiDrawerHeader";

interface MiInternalDrawerProps {
  open?: boolean;
  width?: number | string;
  title?: string;
  className?: string;
  leftIcon?: string;
  leftIconColor?: string;
  onClose?: () => void;
  color?: string;
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: "text" | "outlined" | "contained";
    color?: string;
    disabled?: boolean;
  }>;
  rightAction?: React.ReactNode;
  children?: React.ReactNode;
  subtitle?: string | string[];
  removeHeaderMargin?: boolean;
  headerContainerStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  unmountOnExit?: boolean;
  horizontalTitles?: boolean;
}

export const MiInternalDrawer: FC<MiInternalDrawerProps> = ({
  open = false,
  width = 300,
  title = "",
  className = "",
  leftIcon,
  leftIconColor,
  onClose,
  color,
  actions = [],
  rightAction,
  children,
  subtitle = "",
  removeHeaderMargin = false,
  headerContainerStyle = {},
  headerStyle = {},
  unmountOnExit = true,
  horizontalTitles = false,
}) => {
  const { classes, theme } = useStyles();

  return (
    <Collapse
      in={open}
      unmountOnExit={unmountOnExit}
      className={`${classes.root} ${open ? className : ""}`}
      orientation={ORIENTATION.HORIZONTAL as CollapseProps["orientation"]}
    >
      <Box className={classes.internalDrawerContainer} sx={{ width }}>
        {(title || onClose) && (
          <Box className={classes.headerContainer}>
            <MiDrawerHeader
              title={title}
              subtitle={subtitle}
              leftIcon={leftIcon}
              leftIconColor={leftIconColor}
              color={color}
              removeMargin={removeHeaderMargin}
              containerStyle={headerContainerStyle}
              headerStyle={headerStyle}
              horizontalTitles={horizontalTitles}
              {...(rightAction
                ? { rightAction }
                : {
                    rightIcon: onClose ? mdiClose : null,
                    rightIconColor: (theme.palette.grey as any).main,
                    onRightIconClick: onClose,
                  })}
            />
          </Box>
        )}
        <Box className={classes.contentContainer}>{children}</Box>
        {actions.length > 0 && (
          <Box className={classes.actionContainer}>
            <MiActions actions={actions} />
          </Box>
        )}
      </Box>
    </Collapse>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    boxShadow: `-4px 0px 10px -8px ${theme.palette.grey.accent}`,
  },
  internalDrawerContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    transition: "width 0.25s ease-in-out",
    backgroundColor: theme.palette.common.white,
  },
  headerContainer: {
    position: "sticky",
    top: 0,
  },
  contentContainer: { display: "flex", flex: 1, overflowY: "auto" },
  actionContainer: {
    position: "sticky",
    bottom: "0",
    minHeight: 57,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "4px 16px",
    gap: 16,
    borderTop: `1px solid ${theme.palette.grey.lightAccent}`,
    boxShadow: `0px 2px 12px 0px ${theme.palette.grey.accent}`,
  },
}));
